import { useState, useEffect, useCallback } from "react";
import { salesApi, type Sale } from "../api/sales";
import * as XLSX from "xlsx";
import "./Sales.css";

type Filter = "all" | "active" | "cancelled";

export default function Sales() {
  const [sales, setSales] = useState<Sale[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const loadSales = useCallback(async () => {
    setLoading(true);
    const result = await salesApi.getAll();
    if (result.error) {
      setError(result.error);
    } else {
      setSales(result.data ?? []);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadSales(); }, [loadSales]);

  const handleCancel = async (id: number) => {
    if (!confirm("¿Cancelar esta venta? Los productos volverán al inventario.")) return;

    const res = await fetch(`/api/sales/${id}/cancel`, { method: "PUT" });
    if (res.ok) {
      await loadSales();
    } else {
      setError("Error al cancelar la venta");
    }
  };

  const filtered = sales.filter((s) => {
    if (filter === "all") return true;
    return s.status === filter;
  });

  const exportToExcel = () => {
    const data = filtered.map((s) => ({
      ID: s.id,
      Fecha: new Date(s.createdAt).toLocaleDateString("es-MX"),
      Teléfono: s.buyerPhone,
      Dirección: s.shippingAddress,
      "Total Transferido": s.totalTransferred,
      "Costo Envío": s.shippingCostPaid,
      "Ganancia Neta": s.netRevenue ?? 0,
      Estado: s.status === "active" ? "Activo" : "Cancelado",
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Ventas");
    XLSX.writeFile(wb, `garment-ventas-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  if (loading) {
    return (
      <div>
        <div className="screen-header"><h1>Sales</h1><p>View and manage your sales history</p></div>
        <p className="sales-status">Cargando ventas…</p>
      </div>
    );
  }

  return (
    <div>
      <div className="screen-header">
        <h1>Sales</h1>
        <p>View and manage your sales history</p>
      </div>

      {error && (
        <div className="sales-error" onClick={() => setError("")}>
          Error: {error} <span className="sales-error__dismiss">x</span>
        </div>
      )}

      {/* ─── Filters + Export ──────────────────── */}
      <div className="sales-toolbar">
        <div className="sales-filters">
          {(["all", "active", "cancelled"] as Filter[]).map((f) => (
            <button
              key={f}
              className={`sales-filter ${filter === f ? "sales-filter--active" : ""}`}
              onClick={() => setFilter(f)}
            >
              {f === "all" ? "Todos" : f === "active" ? "Activos" : "Cancelados"}
            </button>
          ))}
        </div>
        <button className="sales-export-btn" onClick={exportToExcel}>
          <span className="material-symbols-rounded">download</span> Exportar Excel
        </button>
      </div>

      {/* ─── Sales List ────────────────────────── */}
      {filtered.length === 0 ? (
        <p className="sales-empty">Sin ventas{filter !== "all" ? ` ${filter === "active" ? "activas" : "canceladas"}` : ""}.</p>
      ) : (
        <div className="sales-list">
          {filtered.map((sale) => (
            <div
              key={sale.id}
              className={`sales-card ${expandedId === sale.id ? "sales-card--expanded" : ""}`}
              onClick={() => setExpandedId(expandedId === sale.id ? null : sale.id)}
            >
              <div className="sales-card__header">
                <div className="sales-card__info">
                  <span className="sales-card__phone">
                    <span className="material-symbols-rounded">call</span> {sale.buyerPhone.slice(-8)}
                  </span>
                  <span className="sales-card__date">
                    {new Date(sale.createdAt).toLocaleDateString("es-MX", {
                      day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
                    })}
                  </span>
                </div>
                <div className="sales-card__right">
                  <span className="sales-card__revenue">
                    ${(sale.netRevenue ?? 0).toFixed(0)}
                  </span>
                  <span className={`sales-badge sales-badge--${sale.status}`}>
                    {sale.status === "active" ? "Activo" : "Cancelado"}
                  </span>
                </div>
              </div>

              {/* Expanded details */}
              {expandedId === sale.id && (
                <div className="sales-card__details">
                  <div className="sales-detail">
                    <span>Total transferido</span>
                    <span>${sale.totalTransferred}</span>
                  </div>
                  <div className="sales-detail">
                    <span>Costo de envío</span>
                    <span>${sale.shippingCostPaid}</span>
                  </div>
                  <div className="sales-detail">
                    <span>Ganancia neta</span>
                    <span className={((sale.netRevenue ?? 0) >= 0) ? "text-green" : "text-red"}>
                      ${(sale.netRevenue ?? 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="sales-detail">
                    <span>Dirección</span>
                    <span className="sales-detail__address">{sale.shippingAddress}</span>
                  </div>
                  <div className="sales-detail">
                    <span>Teléfono completo</span>
                    <span>{sale.buyerPhone}</span>
                  </div>

                  {sale.status === "active" && (
                    <button
                      className="sales-cancel-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCancel(sale.id);
                      }}
                    >
                      <span className="material-symbols-rounded">cancel</span> Cancelar venta
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
